import { useEffect, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api, type HrRestingResponse } from "../../lib/api";
import { EmptyState } from "../../components/EmptyState";
import { HrStat } from "./_shared";

const TEAL = "#5e7a73";

export default function HeartResting() {
  const [data, setData] = useState<HrRestingResponse | null | "error">(null);

  useEffect(() => {
    api.heart
      .resting(90)
      .then(setData)
      .catch(() => setData("error"));
  }, []);

  const d = data && data !== "error" ? data : null;
  const hasData = d != null && d.series.length > 0;

  // Trend hint: compare latest 7-day avg vs the older baseline.
  const trend =
    d && d.avg7 != null && d.avg30 != null ? d.avg7 - d.avg30 : null;

  return (
    <div className="animate-fade-in">
      <h2 className="section-title mb-1">Resting heart rate</h2>
      <p className="text-ink-subtle text-small mb-4">
        Your lowest sustained rate each day. A steadily lower resting HR usually tracks improving
        fitness; a creeping rise can flag stress, poor sleep, or illness.
      </p>

      {!hasData ? (
        <EmptyState error={data === "error"} title="No resting-rate history yet">
          Each day with heart-rate data adds a point here. Give it a few days of wear to see the
          trend.
        </EmptyState>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-2">
            <HrStat label="Latest" value={d!.latest} unit="bpm" accent />
            <HrStat label="7-day" value={d!.avg7} unit="bpm" />
            <HrStat label="30-day" value={d!.avg30} unit="bpm" />
            <HrStat label="Range" value={d!.min} unit={`–${d!.max} bpm`} />
          </div>

          {trend != null && (
            <p className="mt-2 text-xs text-ink-subtle">
              {trend < -1
                ? `▼ Your 7-day average is ${Math.abs(trend)} bpm below your 30-day baseline — trending down (good).`
                : trend > 1
                  ? `▲ Your 7-day average is ${trend} bpm above your 30-day baseline — worth watching.`
                  : `Holding steady against your 30-day baseline.`}
            </p>
          )}

          <div className="card card-pad mt-5">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={d!.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
                <XAxis dataKey="date" axisLine={false} tickLine={false} fontSize={11} minTickGap={28} />
                <YAxis axisLine={false} tickLine={false} width={32} fontSize={11} domain={["auto", "auto"]} />
                <Tooltip formatter={(v: number) => [`${v} bpm`, "Resting"]} />
                {d!.avg30 != null && (
                  <ReferenceLine y={d!.avg30} stroke="#a8a29e" strokeDasharray="4 4" />
                )}
                <Line type="monotone" dataKey="resting_hr" stroke={TEAL} strokeWidth={2} dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
            <p className="mt-2 text-xs text-ink-subtle">
              Dashed line = 30-day average ({d!.avg30} bpm) · {d!.days_with_data} days of data.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
